import { NotFoundError } from 'utils/customError'

import type {
  IndikatorRefleksiPotensi,
  RefleksiPotensiResponse,
  RefleksiPotensiStatus,
} from 'types/refleksi-potensi'

export const ASSESSMENT_PARTS = 3

const ASSESSMENT_RETENTION_HOURS = 24

export type AssessmentState = 'not-started' | 'draft' | 'expired' | 'completed'

export type RefleksiPotensiViewModel = {
  state: AssessmentState | null
  totalQuestions: number
  answeredQuestions: number
  estimatedDuration: string
  indicators: IndikatorRefleksiPotensi[]
  startedAtLabel: string | null
  deadlineLabel: string | null
}

export const isRefleksiPotensiIneligible = (error: unknown) =>
  error instanceof NotFoundError

const STATUS_STATE: Record<RefleksiPotensiStatus, AssessmentState> = {
  'Belum Mulai': 'not-started',
  Draf: 'draft',
  Kedaluwarsa: 'expired',
  Selesai: 'completed',
}

export const getAssessmentState = (status?: string): AssessmentState | null =>
  STATUS_STATE[status as RefleksiPotensiStatus] ?? null

export const toSafeCount = (value?: number) =>
  Number.isFinite(value) && value > 0 ? Math.floor(value) : 0

export const toValidDate = (value?: string | null) => {
  if (!value) return null

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export const formatAssessmentDateTime = (value?: string | null) => {
  const date = toValidDate(value)
  if (!date) return null

  return new Intl.DateTimeFormat('id-ID', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'Asia/Jakarta',
  }).format(date)
}

export const getDeadlineDate = (startedAt?: string | null) => {
  const startedAtDate = toValidDate(startedAt)
  if (!startedAtDate) return null

  return new Date(
    startedAtDate.getTime() + ASSESSMENT_RETENTION_HOURS * 60 * 60 * 1000
  )
}

export const getRefleksiPotensiViewModel = (
  data?: RefleksiPotensiResponse
): RefleksiPotensiViewModel => {
  const totalQuestions = toSafeCount(data?.jumlahSoal)
  const startedAt = data?.waktuMulaiPengerjaanTerakhir
  const deadline = getDeadlineDate(startedAt)

  return {
    state: getAssessmentState(data?.status),
    totalQuestions,
    answeredQuestions: Math.min(
      toSafeCount(data?.jumlahSoalTerjawab),
      totalQuestions
    ),
    estimatedDuration: data?.estimasiWaktuPengerjaan?.trim() || '—',
    indicators: Array.isArray(data?.indikator) ? data.indikator : [],
    startedAtLabel: formatAssessmentDateTime(startedAt),
    deadlineLabel: formatAssessmentDateTime(deadline?.toISOString()),
  }
}
